import { EditOutlined } from '@ant-design/icons';
import { Button, Form, notification, Tooltip, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import {
  type Key,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { ModelBranchSearchResult } from '@app/api/Branches';
import { useSearchBranchesMutation } from '@app/api/Branches';
import { useLazyGetChainsForSystemQuery } from '@app/api/Chains';
import {
  useLazyGetUsedCitiesQuery,
  useLazyGetUsedCountriesQuery,
} from '@app/api/Dictionaries';
import {
  BRANCHES,
  EDIT_BRANCH,
  EDIT_BRANCH_LABEL,
  ERROR,
  MOBILE,
  PREFIX,
} from '@app/constants';
import { navigation } from '@app/data';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { getSelectorOptions } from '@app/pages/Branches/BranchesList/utils/getSelectorOptions';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NSmallGlobalEntities } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NDictionaries } from '@app/types/Dictionaries';
import type { NNavigation } from '@app/types/Navigation';
import type { NUtils } from '@app/types/Utils';

import { type ItemProps } from '@pages/Branches/BranchesList/data/StatusOptions';
import { adaptBranchesToRedux } from '@pages/Chains/AddChain/utils/adaptBranchesToRedux';

import type { BaseOptionType } from 'antd/es/select';

const { Text } = Typography;
const { useForm } = Form;

type TRegistryBranch = {
  id: number;
  key: Key;
  name: string;
  city: string;
  address: string;
  status: string;
};

type BranchSearchFilter = Omit<
  NBranches.ModelBranchSearchFilter,
  'PageN' | 'BranchesPerPage'
>;

type BranchDictionariesData = {
  chains: NSmallGlobalEntities.ModelItem[];
  countries: Pick<NDictionaries.ModelCountry, 'Id' | 'Name'>[];
  cities: string[];
};

const branchesDictionariesCache = new Map<string, BranchDictionariesData>();
const branchesDictionariesInFlight = new Map<
  string,
  Promise<BranchDictionariesData>
>();
const branchesInitialSearchCache = new Map<
  string,
  {
    data: ModelBranchSearchResult;
    createdAt: number;
  }
>();
const INITIAL_BRANCHES_SEARCH_CACHE_TTL_MS = 2000;

export const BRANCHES_BLOCK = `${PREFIX}${BRANCHES}`;
export const DEFAULT_ROWS_PER_PAGE = 20;

export const useBranchesList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editableChainId = Number(searchParams.get('id')) || null;
  const initialFilter: BranchSearchFilter = useMemo(
    () => ({
      SearchTerm: null,
      ChainIds: editableChainId ? [editableChainId] : null,
      CountryIds: null,
      Cities: null,
      Statuses: null,
    }),
    [editableChainId]
  );
  const [filters, setFilters] = useState<BranchSearchFilter>(initialFilter);
  const { showNotification } = useContext(NotificationContext);
  const { userId, database, activeBranchId } = useUserPrint();
  const {
    userAccessRights: {
      Branches: { IsOn: hasUserRightToModelView, IsEdit: hasUserRight },
    },
  } = useNewUserAccessRights();

  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [defaultChains, setDefaultChains] = useState<
    NSmallGlobalEntities.ModelItem[]
  >([]);
  const [chainsOptions, setChainOptions] = useState<ItemProps[]>([]);
  const [chainsData, setChainData] = useState<string[]>([]);

  const [defaultUsedCountries, setDefaultUsedCountries] = useState<
    Pick<NDictionaries.ModelCountry, 'Id' | 'Name'>[]
  >([]);
  const [countriesOptions, setCountriesOptions] = useState<ItemProps[]>([]);
  const [countriesData, setCountriesData] = useState<string[]>([]);

  const [defaultUsedCities, setDefaultUsedCities] = useState<string[]>([]);
  const [usedCitiesOptions, setUsedCitiesOptions] = useState<ItemProps[]>([]);
  const [usedCitiesData, setUsedCitiesData] = useState<string[]>([]);

  const [selectedChains, setSelectedChains] = useState<BaseOptionType[] | null>(
    null
  );
  const [selectedCountries, setSelectedCountries] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedCities, setSelectedCities] = useState<BaseOptionType[] | null>(
    null
  );
  const [selectedStatuses, setSelectedStatuses] = useState<
    BaseOptionType[] | null
  >(null);

  const [branchesData, setBranchesData] = useState<
    NBranches.BranchForRegistry[]
  >([]);

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const [isFiltersHidden, setIsFiltersHidden] = useState<boolean>(isMobile);

  const [errorType, setErrorType] = useState<NUtils.ErrorTypes>(undefined);
  const [formValues, setFormValues] = useState<BranchSearchFilter>({
    SearchTerm: null,
    ChainIds: null,
    CountryIds: null,
    Cities: null,
    Statuses: null,
  });

  const [initialStatusValue] = useState(['active', 'deleted']);

  const [serverError, setServerError] = useState('');
  const [isLoading, setLoading] = useState(false);

  const [, notificationContextHolder] = notification.useNotification();

  const [form] = useForm();

  const [searchBranches] = useSearchBranchesMutation();
  const [getChainsForSystem] = useLazyGetChainsForSystemQuery();
  const [getUsedCountries] = useLazyGetUsedCountriesQuery();
  const [getUsedCities] = useLazyGetUsedCitiesQuery();

  const branchesSearchInFlight = useMemo(
    () => new Map<string, Promise<Awaited<ModelBranchSearchResult>>>(),
    []
  );

  const isUserPrintReady = Boolean(userId && database && activeBranchId);
  const userPrintKey = useMemo(
    () => (isUserPrintReady ? `${userId}-${database}-${activeBranchId}` : null),
    [activeBranchId, database, isUserPrintReady, userId]
  );
  const canLoadBranches = isUserPrintReady && hasUserRightToModelView;
  const toolbarClassName = useMemo(
    () =>
      isFiltersHidden
        ? `${BRANCHES_BLOCK}__toolbar ${BRANCHES_BLOCK}__toolbar--hidden`
        : `${BRANCHES_BLOCK}__toolbar`,
    [isFiltersHidden]
  );

  const rowClassName = useCallback(
    (row: NBranches.BranchForRegistry) =>
      `${BRANCHES_BLOCK}__row${
        row.isDeleted ? ` ${BRANCHES_BLOCK}__row--archived` : ''
      }`,
    []
  );

  const handleEdit = useCallback(
    (record: TRegistryBranch) => {
      const editPath = (navigation as NNavigation.SidebarItem[]).find(
        ({ id }) => id === EDIT_BRANCH
      )?.path;

      if (!editPath) return;

      navigate(`${editPath}?id=${record.id}`);
    },
    [navigate]
  );

  const columns: ColumnsType<NBranches.BranchForRegistry> = useMemo(
    () => [
      {
        title: <span data-testid="branches-name">Название</span>,
        dataIndex: 'name',
        render: (text, record) => (
          <div
            style={{ opacity: record.status ? '50%' : 1 }}
            data-testid="branch-name"
          >
            {record.status ? (
              <Text>{text}</Text>
            ) : (
              <Link
                to={`/branches/edit/?id=${record.key}`}
                className={`${BRANCHES_BLOCK}__edit`}
              >
                {text}
              </Link>
            )}
            {record.status && (
              <div style={{ color: 'red' }} data-testid="archive-branch">
                В архиве
              </div>
            )}
          </div>
        ),
      },
      {
        title: <span data-testid="branches-city">Город</span>,
        dataIndex: 'city',
        render: (text, record) => (
          <div
            style={{ opacity: record.status ? '50%' : 1 }}
            data-testid="branch-city"
          >
            {text}
          </div>
        ),
      },
      {
        title: <span data-testid="branches-address">Адрес</span>,
        dataIndex: 'address',
        render: (text, record) => (
          <div
            style={{ opacity: record.status ? '50%' : 1 }}
            data-testid="branch-address"
          >
            {text}
          </div>
        ),
      },
      {
        title: <span data-testid="branches-legal-entity">Сеть</span>,
        dataIndex: 'chainId',
        render: (id) => {
          const currentChain = defaultChains?.find(
            (chain) => Number(chain.Id) === Number(id)
          );

          return (
            <Text
              className="no-wrap-text"
              style={{ maxWidth: 240 }}
              type={currentChain?.Name ? undefined : 'secondary'}
            >
              {currentChain?.Name || 'Вне сети'}
            </Text>
          );
        },
        minWidth: 80,
      },
      {
        title: (
          <span data-testid="branches-legal-entity">Юридическое лицо</span>
        ),
        dataIndex: 'legalEntity',
        render: (text) => (
          <span className="no-wrap-text" style={{ maxWidth: 240 }}>
            {text || '-'}
          </span>
        ),
        minWidth: 120,
      },
      {
        title: <span data-testid="branches-users">Пользователи</span>,
        dataIndex: 'countUsers',
        render: (text, record) => (
          <div
            style={{ opacity: record.status ? '50%' : 1 }}
            data-testid="branch-users"
          >
            {text}
          </div>
        ),
      },
      hasUserRight
        ? {
            render: (record: NBranches.BranchForRegistry) =>
              !record.status && (
                <Tooltip title={EDIT_BRANCH_LABEL}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() =>
                      handleEdit({
                        id: Number(record.key),
                        key: record.key,
                        name: record.name,
                        city: record.city,
                        address: record.address,
                        status: record.status,
                      } as TRegistryBranch)
                    }
                    style={{ opacity: record.status ? '50%' : 1 }}
                    data-testid="edit-branch"
                  />
                </Tooltip>
              ),
            width: '10%',
          }
        : {},
    ],
    [handleEdit, hasUserRight, defaultChains]
  );

  const hideFilters = useCallback(() => {
    setIsFiltersHidden((prev) => !prev);
  }, []);

  const applyDictionariesData = useCallback(
    ({ chains, countries, cities }: BranchDictionariesData) => {
      setDefaultChains(chains);
      setDefaultUsedCountries(countries);
      setDefaultUsedCities(cities);
    },
    []
  );

  const fetchDictionaries =
    useCallback(async (): Promise<BranchDictionariesData> => {
      if (!activeBranchId) {
        return { chains: [], countries: [], cities: [] };
      }

      const [fetchedChains, fetchedUsedCountries, fetchedCities] =
        await Promise.all([
          getChainsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap(),
          getUsedCountries({
            ActiveBranchId: activeBranchId,
          }).unwrap(),
          getUsedCities({
            ActiveBranchId: activeBranchId,
          }).unwrap(),
        ]);

      return {
        chains: fetchedChains,
        countries: fetchedUsedCountries.map(({ Id, Name }) => ({
          Id,
          Name: Name ?? '',
        })),
        cities: fetchedCities,
      };
    }, [getChainsForSystem, getUsedCountries, getUsedCities, activeBranchId]);

  const initChainsOptions = useCallback(() => {
    getSelectorOptions({
      flag: 'chains',
      defaultData: defaultChains || [],
      setOptions: setChainOptions,
      setData: setChainData,
      form,
      editableChainId,
    });
  }, [editableChainId, defaultChains, form]);

  const initCountriesOptions = useCallback(() => {
    getSelectorOptions({
      flag: 'countries',
      defaultData: defaultUsedCountries || [],
      setOptions: setCountriesOptions,
      setData: setCountriesData,
      form,
    });
  }, [defaultUsedCountries, form]);

  const initCitiesOptions = useCallback(() => {
    getSelectorOptions({
      flag: 'cities',
      defaultData: defaultUsedCities || [],
      setOptions: setUsedCitiesOptions,
      setData: setUsedCitiesData,
      form,
    });
  }, [defaultUsedCities, form]);

  const loadBranches = useCallback(async () => {
    try {
      setLoading(true);

      const requestData = {
        ...filters,
        BranchesPerPage: rowsPerPage,
        PageN: pageNumber,
      };
      const requestKey = JSON.stringify({
        userId,
        database,
        activeBranchId,
        requestData,
      });
      const isInitialRequest =
        pageNumber === 1 &&
        rowsPerPage === DEFAULT_ROWS_PER_PAGE &&
        JSON.stringify(filters) === JSON.stringify(initialFilter);

      const cachedInitialResponse = isInitialRequest
        ? branchesInitialSearchCache.get(requestKey)
        : null;

      const recentInitialResponse =
        cachedInitialResponse &&
        Date.now() - cachedInitialResponse.createdAt <=
          INITIAL_BRANCHES_SEARCH_CACHE_TTL_MS
          ? cachedInitialResponse.data
          : null;

      if (cachedInitialResponse && !recentInitialResponse) {
        branchesInitialSearchCache.delete(requestKey);
      }

      let response: ModelBranchSearchResult;

      if (recentInitialResponse) {
        response = recentInitialResponse;
      } else {
        const existingRequest = branchesSearchInFlight.get(requestKey);

        if (existingRequest) {
          response = await existingRequest;
        } else {
          const request = searchBranches({
            ActiveBranchId: activeBranchId,
            data: requestData,
          }).unwrap();

          branchesSearchInFlight.set(requestKey, request);

          try {
            response = await request;
          } finally {
            branchesSearchInFlight.delete(requestKey);
          }
        }
      }

      const { Branches, PageN, CountBranches } = response;

      if (isInitialRequest) {
        branchesInitialSearchCache.set(requestKey, {
          data: response,
          createdAt: Date.now(),
        });
      }

      setRowsCount(CountBranches);
      setPageNumber(PageN);

      const adaptedBranches =
        Branches?.map((item) => {
          const branch = adaptBranchesToRedux(item, 'toRegistry');

          return {
            ...branch,
            city: branch.city ?? '',
            name: branch.name ?? '',
            address: branch.address ?? '',
            legalEntity: branch.legalEntity ?? '',
            countUsers: branch.countUsers ?? 0,
          };
        }) || [];

      setBranchesData(adaptedBranches);
    } catch (error) {
      setErrorType(ERROR);
      setServerError(handleCatchError(error));
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    rowsPerPage,
    pageNumber,
    userId,
    database,
    activeBranchId,
    initialFilter,
    branchesSearchInFlight,
    searchBranches,
  ]);

  const loadInitialBranches = useCallback(async () => {
    setFormValues(initialFilter);
    setFilters(initialFilter);
    setPageNumber(1);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
  }, [initialFilter]);

  const handleTableChange = useCallback(
    async (page: number, pageSize: number) => {
      const nextPage = pageSize === rowsPerPage ? page : 1;
      const nextPageSize = pageSize;

      setPageNumber(nextPage);
      setRowsPerPage(nextPageSize);
      setFilters(formValues);
    },
    [formValues, rowsPerPage]
  );

  const submitForm = useCallback(
    async (values: { search: string }) => {
      const trimmedQuery = values.search?.trim();

      const newFormValues: BranchSearchFilter = {
        SearchTerm: trimmedQuery?.length ? trimmedQuery : null,
        ChainIds: selectedChains?.length
          ? selectedChains.map((chain) => Number(chain.value))
          : null,
        CountryIds: selectedCountries?.length
          ? selectedCountries.map((country) => Number(country.value))
          : null,
        Cities: selectedCities?.length
          ? selectedCities.map((city) => String(city.value))
          : null,
        Statuses: selectedStatuses?.length
          ? selectedStatuses.map((status) => String(status.value))
          : null,
      };

      setFormValues(newFormValues);
      setFilters(newFormValues);
      setPageNumber(1);
    },
    [selectedChains, selectedCountries, selectedCities, selectedStatuses]
  );

  const handleReset = useCallback(() => {
    setSelectedChains([]);
    setSelectedCities([]);
    setSelectedCountries([]);
    setSelectedStatuses([]);
    form.resetFields();

    const resetFilter: BranchSearchFilter = {
      SearchTerm: null,
      ChainIds: editableChainId ? [editableChainId] : null,
      CountryIds: null,
      Cities: null,
      Statuses: null,
    };

    setFormValues(resetFilter);
    setPageNumber(1);
    setRowsPerPage(DEFAULT_ROWS_PER_PAGE);

    setFilters(resetFilter);
  }, [editableChainId, form]);

  const handleSubmitByEnter = useCallback(() => {
    form.submit();
  }, [form]);

  const isFiltersHiddenHandler = useCallback(() => {
    setIsFiltersHidden(isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!isUserPrintReady) return;
    loadInitialBranches();
  }, [loadInitialBranches, isUserPrintReady]);

  useEffect(() => {
    if (!isUserPrintReady || !userPrintKey) return;
    let cancelled = false;

    const loadDictionaries = async () => {
      try {
        const cachedData = branchesDictionariesCache.get(userPrintKey);
        if (cachedData) {
          if (!cancelled) {
            applyDictionariesData(cachedData);
          }
          return;
        }

        const existingPromise = branchesDictionariesInFlight.get(userPrintKey);
        if (existingPromise) {
          const data = await existingPromise;

          if (!cancelled) {
            applyDictionariesData(data);
          }
          return;
        }

        const request = fetchDictionaries();
        branchesDictionariesInFlight.set(userPrintKey, request);

        try {
          const data = await request;
          branchesDictionariesCache.set(userPrintKey, data);

          if (!cancelled) {
            applyDictionariesData(data);
          }
        } finally {
          branchesDictionariesInFlight.delete(userPrintKey);
        }
      } catch (error) {
        if (!cancelled) {
          showNotification(error);
        }
      }
    };

    void loadDictionaries();

    return () => {
      cancelled = true;
    };
  }, [
    applyDictionariesData,
    fetchDictionaries,
    isUserPrintReady,
    showNotification,
    userPrintKey,
  ]);

  useEffect(() => {
    if (!isUserPrintReady) return;
    initChainsOptions();
  }, [initChainsOptions, isUserPrintReady]);

  useEffect(() => {
    if (!isUserPrintReady) return;
    initCountriesOptions();
  }, [initCountriesOptions, isUserPrintReady]);

  useEffect(() => {
    if (!isUserPrintReady) return;
    initCitiesOptions();
  }, [initCitiesOptions, isUserPrintReady]);

  useEffect(() => {
    if (!canLoadBranches) return;
    loadBranches();
  }, [loadBranches, canLoadBranches]);

  useEffect(() => {
    if (!isUserPrintReady) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSubmitByEnter();
      }
    };

    window.addEventListener('keyup', listener);

    return () => {
      window.removeEventListener('keyup', listener);
    };
  }, [handleSubmitByEnter, isUserPrintReady]);

  return {
    form,
    isMobile,
    toolbarClassName,
    hideFilters,
    columns,
    isLoading,
    branchesData,
    rowsCount,
    rowsPerPage,
    pageNumber,
    handleTableChange,
    submitForm,
    handleReset,
    errorType,
    serverError,
    setServerError,
    initialStatusValue,
    chainsOptions,
    countriesOptions,
    usedCitiesOptions,
    selectedChains,
    setSelectedChains,
    selectedCountries,
    setSelectedCountries,
    selectedCities,
    setSelectedCities,
    selectedStatuses,
    setSelectedStatuses,
    chainsData,
    countriesData,
    usedCitiesData,
    notificationContextHolder,
    hasUserRightToModelView,
    rowClassName,
    isFiltersHiddenHandler,
  };
};
